import { Entity } from "game-common/models"
import { randomAppearanceFor } from "game-common/lpc/lpc"
import { forestGrassTile } from "game-common/room/ember/grass_art"
import { Container, Graphics, Sprite, Text, Texture } from "pixi.js"
import { isMobile } from "../../../../client_util"
import { contentSheetResource } from "../content_textures"
import { LpcComposite } from "../sprites/lpc_composite"
import { TextRenderer } from "../text/text_renderer"
import { StaminaMeter } from "./stamina_meter"
import { span } from "./chapter_reward_motion"
import { DemoCursor, drawDioramaFrame, TutorialDemo } from "./tutorial_demo"
import { GOLD, INK, MUTED, RIM } from "./build_board"

export const SPRINT_DEMO_MS = 7600
const RUN_AT = 1800, REST_AT = 4600
const X = 22, Y = 92, W = 320, H = 192

/** The body, keys and stamina all follow the same replayable clock. */
export const sprintTutorialPose = (elapsedMs: number) => {
    const t = Math.max(0, Math.min(SPRINT_DEMO_MS, elapsedMs))
    const sprinting = t >= RUN_AT && t < REST_AT - 200
    const walking = t < RUN_AT - 200
    return {
        step: t < RUN_AT ? 0 : t < REST_AT ? 1 : 2,
        moving: walking || sprinting,
        sprinting,
        x: 42 + 48 * span(t, 200, 1600) + 134 * span(t, RUN_AT, REST_AT - 200),
        stamina: t < RUN_AT ? 1 : t < REST_AT
            ? 1 - 0.85 * span(t, RUN_AT, REST_AT - 200)
            : 0.15 + 0.85 * span(t, REST_AT + 200, SPRINT_DEMO_MS - 300),
        done: t >= SPRINT_DEMO_MS - 200,
    }
}

/** A walking/running diorama in the same timber window as the building lesson. */
export class SprintTutorial extends TutorialDemo {
    private scene = new Container()
    private art: Sprite[] = []
    private runner = new LpcComposite("sprint-tutorial")
    private body = new Entity()
    private controls = new Graphics()
    private shift: Text
    private direction: Text
    private stamina = new StaminaMeter()
    private dust = new Graphics()

    constructor(hover: (over: boolean) => void, dismiss: () => void, touch = isMobile(), player?: Entity) {
        super({
            width: 364, height: 378, padX: X, contentWidth: W,
            captionY: 300, captionH: 64, overline: "MOVEMENT", title: "Make haste",
            steps: {
                labels: ["MOVE", "SPRINT", "RECOVER"],
                litAt: [0, RUN_AT, REST_AT], doneAt: [1500, 4350, 7300],
                linkFill: [[1500, RUN_AT], [4350, REST_AT]],
            },
            durationMs: SPRINT_DEMO_MS, replayAt: SPRINT_DEMO_MS - 100,
            manualStepStarts: [0, RUN_AT, REST_AT],
        }, hover, dismiss)
        this.touch = touch
        this.scene.position.set(X, Y)
        this.content.addChild(this.scene)
        for (let y = 0; y < 6; y++) for (let x = 0; x < 10; x++) {
            const tile = new Sprite(Texture.EMPTY)
            tile.position.set(x * 32, y * 32)
            this.art.push(tile)
            this.scene.addChild(tile)
        }
        this.scene.addChild(this.dust, this.runner)
        this.body.entityType = "player"
        this.body.appearance = player?.appearance || randomAppearanceFor(undefined, "human", "male")
        if (player?.inventory) this.body.inventory = player.inventory
        this.body.weapon = { weaponType: "none" }
        this.body.movement.angle = Math.PI * 2
        this.runner.updatePaperdoll(this.body)
        this.stamina.position.set(W - 104, 12)
        this.scene.addChild(this.stamina, this.controls)
        const label = (text: string, x: number) => {
            const node = new TextRenderer().fontSize(11).bold().strokeThickness(0).render(text) as Text
            node.position.set(x, 163)
            node.anchor.set(0.5)
            this.scene.addChild(node)
            return node
        }
        this.shift = label(touch ? "MOVE STICK" : "SHIFT", 65)
        this.direction = label(touch ? "TO THE RIM" : "+   D  →", 174)
        const frame = new Graphics()
        drawDioramaFrame(frame, X, Y, W, H)
        this.content.addChild(frame)
        this.animate(0)
    }

    protected cursorAt(_t: number): DemoCursor { return { x: 0, y: 0, pressed: false, alpha: 0 } }
    protected captionAt(t: number) {
        const pose = sprintTutorialPose(t)
        const captions = this.touch ? [
            "Push the movement stick to walk.",
            "Push it to the rim to run faster.",
            "Ease off to recover stamina. Watch the bar beside your hearts.",
        ] : [
            "Hold WASD or an arrow key to walk.",
            "Hold SHIFT + a direction to run faster.",
            "Release SHIFT to recover stamina. Watch the bar beside your hearts.",
        ]
        return { text: captions[pose.step], done: pose.done }
    }
    protected rewind() { /* The scene is entirely derived from elapsed time. */ }

    protected poseScene(t: number, deltaMs: number) {
        const pose = sprintTutorialPose(t)
        this.art.forEach((tile, i) => {
            const frame = forestGrassTile(i % 10, Math.floor(i / 10))
            const texture = contentSheetResource("forest-grass")?.textures?.[`forest-grass-${frame}`] || Texture.EMPTY
            if (tile.texture !== texture) { tile.texture = texture; tile.width = tile.height = 32 }
        })
        this.runner.position.set(pose.x, 91)
        // The real sprite driver observes position changes, not just the moving flag.
        this.body.location = { x: pose.x, y: 91 }
        this.body.speed = pose.sprinting ? 160 : 100
        this.body.movement.moving = pose.moving
        this.body.boosted = pose.sprinting
        this.body.speed2 = { x: pose.moving ? 1 : 0, y: 0 }
        this.runner.doMovement(this.body)
        this.stamina.update(pose.stamina)
        this.stamina.animate(deltaMs)
        const g = this.controls.clear()
        ;[{ x: 20, w: 90, active: pose.sprinting }, { x: 122, w: 104, active: pose.moving }].forEach(key => {
            g.lineStyle(1, key.active ? GOLD : RIM, 0.95).beginFill(key.active ? 0x594326 : 0x1b1512, 0.95)
                .drawRoundedRect(key.x, 149, key.w, 28, 4).endFill()
            g.lineStyle(1, INK, key.active ? 0.25 : 0.08).moveTo(key.x + 5, 151).lineTo(key.x + key.w - 5, 151)
        })
        this.shift.tint = pose.sprinting ? INK : MUTED
        this.direction.tint = pose.moving ? INK : MUTED
        this.dust.clear()
        if (pose.sprinting) for (let i = 0; i < 5; i++) {
            const phase = ((t / 500 + i / 5) % 1)
            this.dust.beginFill(0xcbb49a, (1 - phase) * 0.35)
                .drawCircle(pose.x - 8 - phase * 22, 120 - phase * 6, 1 + phase * 2).endFill()
        }
    }
}
